import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Clock3,
  Droplets,
  LineChart,
  Monitor,
  ShieldCheck,
  Sparkles,
  Target,
  Link2,
  Cpu,
  Bot,
  Globe2,
  Scan,
  Shield,
  Truck,
  TrendingUp,
  Users,
  Wrench,
  FileText,
} from "lucide-react";
import FAQSection from "../components/FAQSection";
import BlogSection from "../components/BlogSection";
import { AnimatedCard } from "../components/AnimatedCard";

const CALENDLY = "https://calendly.com/nvseeds/30min";

export const AiMlDevelopment = () => {
  const aiMlFaqs = [
    {
      question: "Is AI development expensive in 2026?",
      answer:
        "It is more affordable than ever when done correctly. By using Small Language Models and efficient data pipelines, AI operating costs have dropped significantly. The bigger expense is the opportunity cost of not automating.",
    },
    {
      question: "Can you integrate AI with my old legacy systems?",
      answer:
        "Absolutely. We build AI wrappers and middleware that pull data from legacy systems such as SAP or Oracle and feed modern AI models without requiring a full system overhaul.",
    },
    {
      question: "How long does a typical AI project take?",
      answer:
        "A proof of concept usually takes 4 to 6 weeks. A full production-grade rollout generally ranges from 3 to 6 months depending on data complexity and integration scope.",
    },
    {
      question: "What about data privacy?",
      answer:
        "Data privacy is a priority. We offer local LLM hosting and VPC deployments so proprietary business data can stay within your secure environment.",
    },
  ];

  const stats = [
    { label: "Projects delivered", value: "500+", sub: "Across AI & software" },
    { label: "Global reach", value: "20+", sub: "Countries served" },
    { label: "Client satisfaction", value: "98%", sub: "Outcome-focused teams" },
    { label: "Speed to value", value: "4–6 wks", sub: "Typical PoC window" },
  ];

  const serviceCards = [
    {
      icon: Sparkles,
      title: "End-to-end AI solutions",
      body:
        "From vision and data to deployment and maintenance — full lifecycle delivery. We align AI investment to measurable business impact, not science projects.",
    },
    {
      icon: Target,
      title: "AI consulting",
      body:
        "Value-first audits tied to KPIs and P&L. We separate quick wins from high-risk moonshots and chart a practical path to production.",
    },
    {
      icon: Link2,
      title: "AI integration & optimization",
      body:
        "Connect models to SAP, Oracle, and modern APIs without ripping out your core stack. Tune cost, latency, and reliability for real workloads.",
    },
    {
      icon: Cpu,
      title: "Machine learning engineering",
      body:
        "Predictive analytics, computer vision, and NLP — built for throughput, monitoring, and cost control in production, not just notebooks.",
    },
    {
      icon: Bot,
      title: "Generative AI & agents",
      body:
        "Tool-using agents, guardrails, and SLM strategies so automation executes work — not just chat — with ownership of your architecture.",
    },
    {
      icon: Scan,
      title: "Vision, speech & documents",
      body:
        "Intelligent document processing, image workflows, and speech interfaces where unstructured data becomes operational signal.",
    },
  ];

  const aiUseCases = [
    "Customer Support AI",
    "Sales AI Agents",
    "Document Processing",
    "Predictive Analytics",
    "Fraud Detection",
    "Recommendation Systems",
    "Workflow Automation",
    "Data Intelligence",
  ];

  const howWeDeliver = [
    {
      icon: Target,
      title: "AI Strategy & System Design",
      text: "We identify high-impact use cases, assess data readiness, and define the right AI architecture aligned with your objectives.",
      bullets: [
        "Use-case prioritization based on ROI potential",
        "Data availability, quality, and pipeline assessment",
        "Architecture design (LLM, SLM, RAG, or agent systems)",
      ],
      outcome: "Clear roadmap with measurable success metrics",
    },
    {
      icon: ShieldCheck,
      title: "Secure Data & System Integration",
      text: "We integrate AI into your existing systems with enterprise-grade security, compliance, and governance.",
      bullets: [
        "API-first integrations with internal platforms",
        "Data pipelines, transformation, and orchestration",
        "Access control, encryption, and auditability",
      ],
      outcome: "AI systems that are secure, compliant, and production-ready",
    },
    {
      icon: Cpu,
      title: "Modern AI Architecture",
      text: "We design AI systems using the right combination of models and patterns—not generic LLM wrappers.",
      bullets: [
        "SLMs for cost-efficient, domain-specific tasks",
        "RAG pipelines for accurate, grounded outputs",
        "Agent architectures for autonomous task execution",
      ],
      outcome: "Scalable AI systems optimized for cost, accuracy, and performance",
    },
    {
      icon: Sparkles,
      title: "AI-Native Delivery & Optimization",
      text: "We deliver AI through iterative experimentation, validation, and continuous improvement.",
      bullets: [
        "Rapid prototyping with real-world data",
        "Human-in-the-loop validation where needed",
        "Monitoring, evaluation, and performance tuning",
      ],
      outcome: "Reliable AI systems that improve over time",
    },
  ];

  const deliveryStats = [
    { icon: Bot, value: "500+", label: "AI Projects Delivered", sub: "Across industries" },
    { icon: Users, value: "98%", label: "Client Satisfaction", sub: "Long-term partnerships" },
    { icon: Clock3, value: "4-6 Weeks", label: "MVP Development", sub: "From idea to production" },
    { icon: Globe2, value: "Global", label: "Enterprise Clients", sub: "Trusted worldwide" },
  ];

  const whyPartner = [
    { icon: Cpu, title: "Full-stack AI", text: "Data pipelines through UX — complete systems, not isolated demos." },
    { icon: Users, title: "Elastic teams", text: "Sprint capacity or dedicated squads as your roadmap demands." },
    { icon: Shield, title: "Privacy-first options", text: "VPC and local hosting when your data cannot leave your boundary." },
    { icon: Sparkles, title: "Future-ready", text: "Architecture that can absorb 2027–2030 model and tooling shifts." },
  ];

  return (
    <>
      <Helmet>
        <title>AI & Machine Learning Development Services | Novaseeds Technologies</title>
        <meta
          name="description"
          content="NV Seeds delivers AI app and ML development — consulting, custom models, agents, and production integration with measurable ROI and strong data governance."
        />
      </Helmet>

      <div className="min-h-screen pt-28 pb-16 md:pb-24 bg-background">
        <section className="relative overflow-hidden border-b border-[#E2E8F0]/80 bg-gradient-to-b from-[#EFF6FF] via-[#F8FAFC] to-background">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(37,99,235,0.14),transparent)] pointer-events-none" />
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(29,78,216,0.10)_0%,rgba(29,78,216,0.04)_35%,transparent_70%)] pointer-events-none" />
          <div className="container relative mx-auto px-4 md:px-6 max-w-7xl pt-10 pb-14 md:pt-14 md:pb-20">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Services · AI &amp; ML
            </p>
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
              <div className="lg:col-span-7 max-w-4xl">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.08]">
                  AI &amp; Machine{" "}
                  <span className="text-gradient">Learning</span>
                </h1>
                <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                  Intelligent, data-driven applications with meaningful automation, insight,
                  and enhanced experiences — built for ROI, not slide decks.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition hover:bg-blue-700"
                  >
                    Connect With Us
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={CALENDLY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-card px-7 py-3.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-[#F8FAFC]"
                  >
                    Book An Appointment
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_8px_24px_rgba(15,23,42,0.08)] bg-white">
                  <img
                    src="/ai-hero-right.png"
                    alt="AI visualization artwork"
                    className="w-full h-[250px] md:h-[300px] lg:h-[340px] object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 overflow-visible">
              {stats.map((s, i) => (
                <AnimatedCard
                  key={s.label}
                  index={i}
                  slideFrom="alternate"
                  className="rounded-2xl border border-[#E2E8F0] bg-card/90 backdrop-blur-sm p-4 md:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{s.value}</p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{s.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
                </AnimatedCard>
              ))}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-16 md:space-y-20">
          <section className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start pt-4">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                The ROI era — not the hype era
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                By 2026, the AI hype has evaporated and been replaced by a more demanding reality:
                leaders want cost-per-acquisition impact and agents that complete work without
                constant human intervention.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                At NV Seeds, we have moved beyond speculative prototypes. We turn machine learning
                and GenAI into repeatable advantages through consulting, custom models, and
                production-grade automation integrated into your products and operations.
              </p>
            </div>
            <AnimatedCard
              index={0}
              className="lg:col-span-5 rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
                Why NV Seeds
              </p>
              <ul className="space-y-4">
                {[
                  "Domain-tuned models and SLM economics — not generic LLM bills",
                  "Guardrails, validation, and human-in-the-loop where trust matters",
                  "Ship complete systems: data, APIs, UX, and monitoring",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </AnimatedCard>
          </section>

          <div className="relative overflow-hidden py-8 md:py-10">
            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-screen -translate-x-1/2 bg-[#F1F5F9] -z-10" />
            <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl">
              <div className="grid lg:grid-cols-12 gap-5 items-center">
                <motion.div
                  initial={{ x: 0 }}
                  whileInView={{ x: "20%" }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  viewport={{ once: false, amount: 0.35 }}
                  className="lg:col-span-6 lg:relative lg:z-20 rounded-2xl bg-white p-5 md:p-7 lg:min-h-[420px]"
                >
                  <p className="text-sm font-semibold text-blue-600 mb-3">2026 AI Shift</p>
                  <h2 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">
                    The 2026 inflection: chatbots → autonomous agents
                  </h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    AI has moved from answering prompts to reliably executing business workflows with
                    measurable outcomes.
                  </p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div>
                      <p className="text-base font-semibold text-foreground mb-2">2024</p>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• Generic assistants</li>
                        <li>• Retrieval-heavy interactions</li>
                        <li>• Mostly static process steps</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-base font-semibold text-blue-600 mb-2">2026</p>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• Domain-focused agent systems</li>
                        <li>• Task execution with tool actions</li>
                        <li>• Event-aware real-time automation</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
                <div className="lg:col-span-6 lg:relative lg:z-10 rounded-2xl overflow-hidden">
                  <img
                    src="/ai-mind.jpeg"
                    alt="AI brain network visualization"
                    className="w-full object-cover min-h-[340px] lg:min-h-[560px]"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                Our services
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Innovative AI solutions
              </h2>
              <p className="mt-3 text-muted-foreground">
                Comprehensive capabilities across the AI development lifecycle — strategy through production.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
              {serviceCards.map(({ icon: Icon, title, body }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  slideFrom="alternate"
                  hoverScale={1.04}
                  hoverLift={-10}
                  className="group rounded-3xl border border-[#E2E8F0] bg-card p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-xl hover:border-blue-200/60"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 mb-5 transition-colors group-hover:bg-blue-100/80 group-hover:scale-110 duration-300">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">{body}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section className="bg-[#F8FAFC] rounded-3xl px-5 py-10 md:px-8 md:py-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                AI Use Cases
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aiUseCases.map((item, i) => (
                <AnimatedCard
                  key={item}
                  index={i}
                  className="rounded-xl bg-white border border-[#E2E8F0] px-4 py-4 text-base text-foreground shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
                >
                  {item}
                </AnimatedCard>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                How we deliver
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                World-class AI applications
              </h2>
              <p className="mt-3 text-muted-foreground">
                Custom AI development designed to simplify operations, sharpen decisions, and support revenue growth.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              {howWeDeliver.map(({ icon: Icon, title, text, bullets, outcome }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -32 : 32, y: 14 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ duration: 0.55, delay: i * 0.08, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.32 }}
                  className="rounded-2xl border border-[#DCE5F3] bg-white p-5 md:p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <Icon className="h-6 w-6" strokeWidth={1.75} />
                    </div>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                  <ul className="mt-4 space-y-2">
                    {bullets.map((line) => (
                      <li key={line} className="flex gap-2 text-sm text-muted-foreground">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 rounded-xl bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
                    <span className="font-semibold">Outcome:</span> {outcome}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {deliveryStats.map(({ icon: Icon, value, label, sub }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.12 + i * 0.08, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.4 }}
                  className="rounded-xl border border-[#DCE5F3] bg-white px-4 py-3.5"
                >
                  <div className="flex items-center gap-2 text-blue-600">
                    <Icon className="h-4.5 w-4.5" strokeWidth={1.9} />
                    <span className="text-xl font-bold">{value}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Why partner with NV Seeds
              </h2>
              <p className="mt-3 text-muted-foreground">
                Deep custom software capability plus modern AI implementation — production systems, not demos.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 overflow-visible">
              {whyPartner.map(({ icon: Icon, title, text }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  className="rounded-2xl border border-[#E2E8F0] bg-card p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                >
                  <Icon className="h-8 w-8 text-blue-600 mb-3" strokeWidth={1.5} />
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          <div className="overflow-hidden rounded-3xl border border-[#E2E8F0] bg-card shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
            <div className="grid lg:grid-cols-12">
              <div className="lg:col-span-8 border-b border-[#E2E8F0] bg-white p-6 sm:p-8 md:p-10 lg:border-b-0 lg:border-r">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-700">
                  <BookOpen className="h-4 w-4" aria-hidden />
                  Case study
                </div>
                <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl md:text-4xl">
                  From Manual Logistics to{" "}
                  <span className="text-blue-600">AI-Driven</span> Decisioning
                </h2>
                <p className="mt-4 max-w-3xl text-base text-muted-foreground md:text-lg">
                  How a mid-sized logistics company reduced fuel costs, improved delivery efficiency, and
                  achieved ROI in just 4 weeks using AI automation.
                </p>

                <div className="mt-10 space-y-10">
                  <div className="flex gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <Monitor className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">The Challenge</h3>
                      <p className="mt-2 text-muted-foreground">
                        Legacy systems couldn&apos;t process real-time weather, traffic, and port
                        delays—leading to inefficient routing and rising fuel costs.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <Wrench className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-foreground">The Solution</h3>
                      <ul className="mt-3 space-y-2.5 text-muted-foreground">
                        {[
                          "Centralized 5+ years of logistics data",
                          "Built ML-based delivery risk scoring models",
                          "Introduced AI-powered dispatch decision interface",
                        ].map((line) => (
                          <li key={line} className="flex gap-2.5">
                            <Check className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" strokeWidth={2.5} />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div>
                    <div className="flex gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                        <TrendingUp className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Business Impact</h3>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      {[
                        {
                          icon: Droplets,
                          value: "22%",
                          label: "Fuel Cost Reduction",
                        },
                        {
                          icon: Truck,
                          value: "18%",
                          label: "Faster Deliveries",
                        },
                        {
                          icon: LineChart,
                          value: "4 Weeks",
                          label: "ROI Achieved",
                        },
                      ].map(({ icon: Icon, value, label }, i) => (
                        <motion.div
                          key={label}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.45, delay: i * 0.1, ease: "easeOut" }}
                          viewport={{ once: true, amount: 0.35 }}
                          className="rounded-xl border border-[#E2E8F0] bg-white p-4 text-center shadow-sm"
                        >
                          <Icon className="mx-auto h-6 w-6 text-blue-600" strokeWidth={1.75} aria-hidden />
                          <p className="mt-2 text-2xl font-bold tabular-nums text-blue-600">{value}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{label}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col bg-gradient-to-b from-[#1e40af] to-[#0f172a] p-6 sm:p-8 md:p-10 text-white lg:col-span-4">
                <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                  <FileText className="h-7 w-7 text-white" strokeWidth={1.5} aria-hidden />
                </div>
                <h3 className="text-center text-xl font-semibold sm:text-2xl">AI Implementation Playbook</h3>
                <p className="mt-2 text-center text-sm text-blue-100 md:text-base">
                  Our proven 4-week roadmap to measurable results.
                </p>

                <div className="relative mt-8 flex-1">
                  <div className="absolute bottom-4 left-[15px] top-4 w-px bg-white/35 md:left-[17px]" aria-hidden />
                  <ul className="relative space-y-5">
                    {[
                      { week: "Week 1", text: "Identify high-impact repetitive workflows" },
                      { week: "Week 2", text: "Prepare and structure operational data" },
                      { week: "Week 3", text: "Build and validate MVP with real scenarios" },
                      { week: "Week 4", text: "Deploy, monitor, and optimize for ROI" },
                    ].map((step, i) => (
                      <li key={step.week} className="relative flex gap-4 pl-1">
                        <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#1e40af] text-xs font-bold">
                          {i + 1}
                        </div>
                        <motion.div
                          initial={{ opacity: 0, x: -18 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.45, delay: i * 0.1, ease: "easeOut" }}
                          viewport={{ once: true, amount: 0.4 }}
                          className="min-w-0 flex-1 rounded-lg border border-blue-300/40 bg-white/5 px-3 py-3 backdrop-blur-sm"
                        >
                          <p className="text-sm font-semibold text-blue-200">{step.week}</p>
                          <p className="mt-1 text-sm leading-snug text-white">{step.text}</p>
                        </motion.div>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/contact"
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3.5 text-sm font-semibold text-blue-600 shadow-md transition hover:bg-blue-50"
                >
                  Get Similar Results
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-blue-100 sm:text-sm">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-white" aria-hidden />
                  Trusted by 500+ businesses worldwide
                </p>
              </div>
            </div>
          </div>

          <AnimatedCard
            index={0}
            hoverScale={1.02}
            hoverLift={-6}
            className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-50 to-[#EFF6FF] px-6 py-10 md:px-12 md:py-14 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground">
              Ready to Build AI That Delivers ROI?
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Get a tailored AI strategy for your business.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] hover:bg-blue-700"
              >
                Book An Appointment
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] bg-white px-8 py-3.5 text-sm font-semibold text-foreground hover:bg-[#F8FAFC]"
              >
                Contact us
              </Link>
            </div>
          </AnimatedCard>

          <BlogSection
            categorySlug="software-development-company"
            title="What's New In The Market"
            maxPosts={3}
          />
          <FAQSection faqs={aiMlFaqs} title="Frequently Asked Questions" />
        </div>
      </div>
    </>
  );
};
